import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Details from "./details/details";
import EditOrderDetails from "./edit-order-details/edit-order-details";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderStatusColor,
  orderStatus,
  userType,
} from "../../../utils/Constant";
import Notification from "../../../components/notification/notification";
import OrderIssues from "./issues/issues";
import Inspection from "./inspection/inspection";
import History from "./history/history";
import {
  acceptOrderRequest,
  orderValidateRequest,
  rejectOrderRequest,
} from "../../../utils/APICalls";
import RejectOrderDialog from "./reject-order-dialog";
import {
  getOrderDetailsRequest,
  getOrderIssuesCountRequest,
  getOrderIssuesCountSuccess,
} from "../../../redux/orders/slice";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Loading from "../../../components/loading/loading";
import dayjs from "dayjs";
import eventBus from "../../../utils/EventBus";
import ContactSpectrum from "../../../components/contact-spectrum/constact-spectrum";
import { hasFullAccess } from "../../../utils/permissions";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {data?.label}
      {data?.showWarning && (
        <InfoOutlinedIcon className="tab-label-warning-icon" />
      )}
      {data?.chipText && (
        <Chip
          label={data.chipText}
          size="small"
          sx={{
            ml: 1,
            backgroundColor: data?.isSelected ? "primary.main" : "#00000099",
            color: "white",
            height: 20,
            fontSize: 12,
            fontWeight: 400,
            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      )}
    </Box>
  );
}

export default function OrderDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [changePropertyMode, setChangePropertyMode] = useState(false);
  const [shouldSaveForm, setShouldSaveForm] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectDialogLoading, setRejectDialogLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isTimeNegative, setIsTimeNegative] = useState(false);
  const [issuesCount, setIssuesCount] = useState<{
    openIssuesCount: number;
    totalIssuesCount: number;
  } | null>(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  // const { order } = useLocation()?.state;
  const { orderId } = useParams();
  const feesSectionRef = useRef<HTMLDivElement>(null);

  const currentUser = useSelector((state: any) => state.user.user?.currentUser);
  const {
    loading: orderLoading,
    orderDetails,
    orderIssuesCount,
  } = useSelector((state: any) => state.orders);

  useEffect(() => {
    eventBus.addListener("inspectionSuccess", onInspectionSuccess);
    eventBus.addListener("moveToRecruiting", onMoveToRecruitingSuccess);
    eventBus.addListener("uploadingAttachment", onUploadingAttachment);
    return () => {
      eventBus.removeListener("inspectionSuccess", onInspectionSuccess);
      eventBus.removeListener("moveToRecruiting", onMoveToRecruitingSuccess);
      eventBus.removeListener("uploadingAttachment", onUploadingAttachment);
    };
  }, []);

  useEffect(() => {
    if (orderDetails?.inspectorDueDate) {
      const now = dayjs();
      const diff = dayjs(orderDetails?.inspectorDueDate).diff(now);

      if (diff > 0) {
        setIsTimeNegative(false);
      } else {
        setIsTimeNegative(true);
      }
      const totalMinutes = Math.floor(diff / (1000 * 60));
      const days = Math.floor(totalMinutes / (60 * 24));
      const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
      const minutes = totalMinutes % 60;
      setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
    } else {
      setTimeRemaining("");
    }
  }, [orderDetails?.inspectorDueDate]);

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderIssuesCountSuccess({ response: null }));
      dispatch(getOrderIssuesCountRequest({ orderId: orderId }));
    }
  }, [orderId]); // eslint-disable-line

  useEffect(() => {
    if (orderIssuesCount) {
      setIssuesCount({
        openIssuesCount: orderIssuesCount.openIssuesCount || 0,
        totalIssuesCount: orderIssuesCount.totalIssuesCount || 0,
      });
    }
  }, [orderIssuesCount]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleEditClick = () => {
    setChangePropertyMode(false);
    setIsEditMode(true);
  };

  const handleChangeProperty = () => {
    setChangePropertyMode(true);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setChangePropertyMode(false);
  };

  const handleSaveEdit = () => {
    setShouldSaveForm(true);
  };

  const handleSaveComplete = () => {
    setShouldSaveForm(false);
    setIsEditMode(false);
    setChangePropertyMode(false);
    setSuccessMessage("Order updated successfully!");
    setShowSuccessNotification(true);
  };

  const handleEditFees = () => {
    setIsEditMode(true);
    setTimeout(() => {
      if (feesSectionRef.current) {
        const elementPosition = feesSectionRef.current.offsetTop;
        const offsetPosition = elementPosition - 0;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 300);
  };

  const getStatusColor = () => {
    return getOrderStatusColor(orderDetails?.orderStatus);
  };

  const validateOrder = async () => {
    setLoading(true);
    const response = await orderValidateRequest(orderDetails?.id);
    setLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderDetailsRequest({ id: orderDetails?.id }));
      setShowSuccessNotification(true);
      setSuccessMessage("Validated Successfully.");
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const acceptOrder = async () => {
    setLoading(true);
    const response = await acceptOrderRequest(orderDetails?.id);
    setLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderDetailsRequest({ id: orderDetails?.id }));
      setShowSuccessNotification(true);
      setSuccessMessage("Order Accepted Successfully.");
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const rejectOrder = async (reason: string) => {
    setRejectDialogLoading(true);
    const response = await rejectOrderRequest(orderDetails?.id, reason);
    setRejectDialogLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderDetailsRequest({ id: orderDetails?.id }));
      setShowRejectDialog(false);
      setSuccessMessage("Order rejected successfully.");
      setShowSuccessNotification(true);
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onInspectionSuccess = (data: any) => {
    setSuccessMessage(
      data?.message
        ? data.message
        : data?.isReview
          ? "QC Review Submitted Successfully."
          : "Inspection Form Submitted Successfully."
    );
    setShowSuccessNotification(true);
  };

  const onMoveToRecruitingSuccess = () => {
    setSuccessMessage("Moved to Recruiting Successfully.");
    setShowSuccessNotification(true);
  };

  const onUploadingAttachment = (isUploading: boolean) => {
    setIsUploadingAttachment(isUploading);
  };

  const currentOrderStatus =
    orderStatus?.[orderDetails?.orderStatus as keyof typeof orderStatus];

  return (
    <>
      {currentUser?.userType !== userType.client && <ContactSpectrum />}
      <Box mt={currentUser?.userType === userType.client ? 2 : 0}>
        <Breadcrumb orderId={orderDetails?.id} orderNumber={orderDetails?.orderNumber}
          clientId={orderDetails?.client?.id} clientName={orderDetails?.client?.clientName}
          inspectorFirmId={orderDetails?.inspectorFirm?.id} inspectorFirmName={orderDetails?.inspectorFirm?.displayName} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ ml: 2, mt: 4 }} variant="h4">
            Order #{orderDetails?.orderNumber}
          </Typography>
          <Box sx={{ ml: 2, mt: 1.5, display: "flex", alignItems: "center" }}>
            {currentUser?.userType !== userType.inspector && (
              <Box sx={{ mr: 3, display: "flex", alignItems: "center" }}>
                <Avatar
                  alt="Avatar"
                  variant="circular"
                  sx={{ width: 24, height: 24 }}
                  src={orderDetails?.client?.logoUrl}
                ></Avatar>
                <Typography sx={{ ml: 0.5 }}>
                  {orderDetails?.client?.clientName || "-"}
                </Typography>
              </Box>
            )}
            {orderDetails?.inspectorFirm ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {orderDetails?.inspectorFirm?.displayName && (
                  <>
                    <Avatar
                      alt="Avatar"
                      variant="circular"
                      sx={{ width: 24, height: 24 }}
                      src={orderDetails?.inspectorFirm?.logoUrl}
                    ></Avatar>
                    <Typography sx={{ ml: 0.5 }}>
                      {orderDetails?.inspectorFirm?.displayName}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              [
                !orderLoading && (
                  <Typography sx={{ ml: 3 }}>No Inspector Assigned</Typography>
                ),
              ]
            )}
            {orderDetails?.orderStatus && (
              <Chip
                label={orderDetails?.orderStatus}
                size="small"
                sx={{
                  ml: 3,
                  backgroundColor: getStatusColor(),
                  color: "white",
                }}
              />
            )}
          </Box>
        </Box>
        {currentUser?.userType !== userType.client &&
          orderDetails?.orderStatus === "InspectorAssigned" && (
            <Box sx={{ display: "flex", mr: 2, alignItems: "center" }}>
              {currentUser?.userType === userType.inspector &&
                timeRemaining && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: isTimeNegative ? "#d32f2f" : "rgba(0, 0, 0, 0.6)",
                      mr: 2,
                      letterSpacing: "0.17px",
                    }}
                  >
                    {isTimeNegative ? "Time elapsed" : "Time Remaining"}:{" "}
                    {timeRemaining}
                  </Typography>
                )}
              <Button
                sx={{ height: 40 }}
                variant="outlined"
                onClick={() => setShowRejectDialog(true)}
                disabled={isEditMode}
              >
                Reject
              </Button>
              <Button
                sx={{ ml: 2, height: 40 }}
                variant="contained"
                onClick={acceptOrder}
                disabled={isEditMode}
              >
                Accept Order
              </Button>
            </Box>
          )}
        {currentUser?.userType === userType.tenant &&
          (orderDetails?.orderStatus === "AssignedQC" ||
            orderDetails?.orderStatus === "FinalQC" ||
            orderDetails?.orderStatus === "DeliveryQC") && (
            <Button
              sx={{ height: 40, mt: 4, width: 160 }}
              variant="contained"
              onClick={() =>
                navigate("inspection-form", {
                  state: { orderId: orderDetails?.id, isQCReview: true },
                })
              }
              disabled={isEditMode}
            >
              QC Review
            </Button>
          )}
      </Box>
      <Box
        sx={{ mt: 3, mx: 2 }}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box>
          {currentUser?.userType === userType.tenant ? (
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="user details tabs"
              variant="scrollable"
              scrollButtons={false}
            >
              <Tab
                className="tab-label"
                label={<TabLabel label="Order Details" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Inspection" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="History" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={
                  <TabLabel
                    label="Issues"
                    showWarning={false}
                    isSelected={value === 3}
                    chipText={
                      issuesCount && issuesCount.totalIssuesCount > 0
                        ? `${issuesCount.openIssuesCount} of ${issuesCount.totalIssuesCount}`
                        : undefined
                    }
                  />
                }
              />
            </Tabs>
          ) : (
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="user details tabs"
              variant="scrollable"
              scrollButtons={false}
            >
              <Tab
                className="tab-label"
                label={<TabLabel label="Order Details" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Inspection" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={
                  <TabLabel
                    label="Issues"
                    showWarning={false}
                    isSelected={value === 2}
                    chipText={
                      issuesCount && issuesCount.totalIssuesCount > 0
                        ? `${issuesCount.openIssuesCount} of ${issuesCount.totalIssuesCount}`
                        : undefined
                    }
                  />
                }
              />
            </Tabs>
          )}
        </Box>
        {value === 0 && (
          <Box>
            {isEditMode ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  sx={{
                    color: "primary.main",
                    borderColor: "primary.main",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "rgba(255, 107, 53, 0.04)",
                    },
                  }}
                >
                  CANCEL
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEdit}
                  disabled={isUploadingAttachment}
                  sx={{
                    ml: 2,
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.main",
                    },
                  }}
                >
                  SAVE CHANGES
                </Button>
              </>
            ) : (
              <>
                {hasFullAccess(currentUser?.roles, "ModifyOrderDetails") &&
                  currentOrderStatus < 81 && (
                    <Button
                      variant="text"
                      startIcon={<EditOutlinedIcon />}
                      onClick={handleEditClick}
                    >
                      Edit
                    </Button>
                  )}
                {(orderDetails?.orderStatus === "AutoValidation" ||
                  orderDetails?.orderStatus === "ManualValidation") &&
                  currentUser?.userType === userType.tenant && (
                    <Button
                      variant="contained"
                      sx={{ ml: 2 }}
                      onClick={validateOrder}
                    >
                      Validate
                    </Button>
                  )}
              </>
            )}
          </Box>
        )}
      </Box>

      <CustomTabPanel value={value} index={0}>
        {isEditMode ? (
          <EditOrderDetails
            orderId={orderDetails?.id}
            shouldSaveForm={shouldSaveForm}
            setShouldSaveForm={() => setShouldSaveForm(false)}
            onSaveComplete={handleSaveComplete}
            orderStatus={
              orderStatus?.[
              orderDetails?.orderStatus as keyof typeof orderStatus
              ]
            }
            feesSectionRef={feesSectionRef}
            changePropertyMode={changePropertyMode}
            onExitChangeProperty={() => setChangePropertyMode(false)}
          />
        ) : (
          <Details
            orderId={orderId}
            onEditFees={handleEditFees}
            onEdit={handleEditClick}
            onChangeProperty={handleChangeProperty}
            orderStatus={currentOrderStatus}
          />
        )}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <Inspection orderId={orderDetails?.id} orderNumber={orderDetails?.orderNumber} />
      </CustomTabPanel>
      {currentUser?.userType === userType.tenant ? (
        <>
          <CustomTabPanel value={value} index={2}>
            <History orderId={orderDetails?.id} navigateToIssues={() => setValue(3)} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <OrderIssues orderId={orderDetails?.id} />
          </CustomTabPanel>
        </>
      ) : (
        <CustomTabPanel value={value} index={2}>
          <OrderIssues orderId={orderDetails?.id} />
        </CustomTabPanel>
      )}

      <RejectOrderDialog
        open={showRejectDialog}
        loading={rejectDialogLoading}
        onClose={() => setShowRejectDialog(false)}
        onSubmit={rejectOrder}
      />
      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          message={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      )}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {loading && <Loading />}
    </>
  );
}
